import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalDate } from "@js-joda/core";
import type { BackendAxiosError } from "services/api/backendError";
import { PAYMENT_METHOD_OPTIONS } from "../shared/staff.paymentMethod.logic";
import { getAgeFromPersonalIdentityNumber } from "web-apps/company/utils/personalIdentityNumber.utils";
import type {
  EditingTab,
  EmploymentSaveMode,
  EmploymentDetailsForm,
  PaymentMethodDetailsForm,
  PersonalDetailsForm,
  ProfilePanelTab,
  SelectOption,
} from "./profilePanel.contract";
import type {
  UseStaffProfilePanelStateParams,
  UseStaffProfilePanelStateResult,
} from "./state/profilePanel.state.contract";
import { toEmploymentOptions } from "./domains/employment/profilePanel.options.logic";
import { resolveStaffProfilePanelErrorMessage } from "./domains/employment/profilePanel.error.logic";
import { toLocalDateOrNull } from "./domains/employment/profilePanel.payload.logic";
import {
  toEmploymentUiRules,
  withNormalizedEmploymentFormByRules,
} from "../shared/staffEmploymentRules.logic";
import { fetchEmployeeNumberForProfilePanel } from "./domains/employment/profilePanel.employeeNumber.logic";
import {
  EMPTY_EMPLOYMENT_DETAILS,
  EMPTY_PAYMENT_METHOD_DETAILS,
  EMPTY_PERSONAL_DETAILS,
  toEmployeeNumberForNewEmployment,
  toEmploymentDetailsForm,
  toPersonalDetailsForm,
} from "./state/profilePanel.state.formMappers.logic";
import {
  canSavePaymentMethod,
  resolvePaymentMethodError,
} from "./state/profilePanel.state.paymentMethod.logic";
import { savePersonalProfile } from "./state/profilePanel.state.personal.logic";
import { performSaveEmployment as runPerformSaveEmployment } from "./state/profilePanel.state.employment.logic";
import { useExecuteSavingAction } from "./state/profilePanel.state.saveAction.logic";
import { useProfilePanelModalsState } from "./state/profilePanel.state.modals.logic";
import { useProfilePanelTemplatesState } from "./state/profilePanel.state.templates.logic";
import {
  toPhoneDigits,
  toTeamOptions,
} from "./state/profilePanel.state.template.logic";
import {
  isPermanentEmployment,
  toPermanentEmploymentDefaults,
} from "../shared/staffPermanentEmploymentDefaults.logic";
import {
  canSaveEmploymentEdit as toCanSaveEmploymentEdit,
  isValidPhoneNumber,
} from "./state/profilePanel.state.validation.logic";

export const useStaffProfilePanelState = (
  params: UseStaffProfilePanelStateParams,
): UseStaffProfilePanelStateResult => {
  const {
    companyId,
    companyAddress,
    companySettings,
    initialTab,
    isOpen,
    onWorkerUpdated,
    profile,
    reloadProfile,
    subscription,
    t,
    unitTeamOptions,
    workerId,
  } = params;
  const [activeTab, setActiveTab] = useState<ProfilePanelTab>(
    initialTab ?? "profile",
  );
  const [editingTab, setEditingTab] = useState<EditingTab>(null);

  const [employmentSaveMode, setEmploymentSaveMode] =
    useState<EmploymentSaveMode>(null);
  const [editedPersonalDetails, setEditedPersonalDetails] =
    useState<PersonalDetailsForm>(EMPTY_PERSONAL_DETAILS);
  const [editedEmploymentDetails, setEditedEmploymentDetails] =
    useState<EmploymentDetailsForm>(EMPTY_EMPLOYMENT_DETAILS);
  const [editedPaymentMethodDetails, setEditedPaymentMethodDetails] =
    useState<PaymentMethodDetailsForm>(EMPTY_PAYMENT_METHOD_DETAILS);
  const [hasTouchedPaymentMethodValue, setHasTouchedPaymentMethodValue] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showLockedEditHint, setShowLockedEditHint] = useState(false);
  const employeeNumberRequestIdRef = useRef(0);
  const employmentRules = useMemo(() => {
    return toEmploymentUiRules(
      editedEmploymentDetails.employmentType,
      editedEmploymentDetails.salaryType,
      subscription,
      companySettings,
    );
  }, [
    companySettings,
    editedEmploymentDetails.employmentType,
    editedEmploymentDetails.salaryType,
    subscription,
  ]);

  const employmentOptions = useMemo(() => {
    return toEmploymentOptions(params.t, employmentRules);
  }, [employmentRules, params.t]);

  const paymentMethodOptions: SelectOption[] = PAYMENT_METHOD_OPTIONS.map(
    (paymentMethodOption) => ({
      value: paymentMethodOption.value,
      label: t(paymentMethodOption.label),
    }),
  );

  const teamOptions = useMemo(() => {
    return toTeamOptions(unitTeamOptions);
  }, [unitTeamOptions]);

  const currentEmploymentStartDate = useMemo(() => {
    return toLocalDateOrNull(profile?.employmentInfo?.current?.startDate);
  }, [profile?.employmentInfo]);
  const currentEmploymentId = profile?.employmentInfo?.current?.id ?? null;
  const workerAge = useMemo(() => {
    return getAgeFromPersonalIdentityNumber(
      profile?.personalIdentityNumber ?? null,
    );
  }, [profile?.personalIdentityNumber]);

  const executeSavingAction = useExecuteSavingAction({
    resolveErrorMessage: (error: BackendAxiosError): string =>
      resolveStaffProfilePanelErrorMessage(error, t, {
        paymentMethodType: editedPaymentMethodDetails.type,
      }),
    setIsSaving,
    setSaveError,
  });

  const {
    employmentTemplates,
    isLoadingEmploymentTemplates,
    selectedEmploymentTemplateId,
    applyEmploymentTemplate,
    templateSalaries: visibleTemplateSalaries,
    selectedTemplateSalaryId,
    applyEmploymentTemplateSalary,
    resetTemplateRequests,
  } = useProfilePanelTemplatesState({
    companyId,
    editingTab,
    employmentSaveMode,
    isOpen,
    setEditedEmploymentDetails,
    setSaveError,
    workerAge,
    workerId,
  });

  const {
    salaryDraftHistory,
    taxDraftHistory,
    effectiveFromDate,
    endEmploymentDate,
    hasEndedEmploymentForCreateFlow,
    isDiscardConfirmOpen,
    isSalaryModalOpen,
    isTaxModalOpen,
    isPaymentMethodModalOpen,
    isEndEmploymentModalOpen,
    isEndEmploymentForCreateModalOpen,
    setEffectiveFromDate,
    setEndEmploymentDate,
    openEndEmploymentForCreateModal,
    handleOpenSalaryModal,
    handleCloseSalaryModal,
    handleConfirmSalaryModal,
    handleAddSalaryModal,
    handleDeleteSalaryModal,
    handleUpdateSalaryDraftValue,
    handleOpenTaxModal,
    handleCloseTaxModal,
    handleConfirmTaxModal,
    handleAddTaxModal,
    handleDeleteTaxModal,
    handleOpenPaymentMethodModal,
    handleClosePaymentMethodModal,
    handleConfirmPaymentMethodModal,
    handleDeletePaymentMethodModal,
    handleOpenEndEmploymentModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
    handleConfirmDiscardClose,
    handleCancelDiscardClose,
  } = useProfilePanelModalsState({
    companyId,
    currentEmploymentId,
    currentEmploymentStartDate,
    editedEmploymentDetails,
    editedPaymentMethodDetails,
    employmentRules,
    executeSavingAction,
    isOpen,
    isSaving,
    onWorkerUpdated,
    profile,
    reloadProfile,
    setEditedEmploymentDetails,
    setEditedPaymentMethodDetails,
    setEditingTab,
    setEmploymentSaveMode,
    setHasTouchedPaymentMethodValue,
    setIsSaving,
    setSaveError,
    translate: t,
    workerId,
  });

  useEffect(() => {
    const resetTab = initialTab ?? "profile";
    if (!isOpen) {
      setActiveTab(resetTab);
      setEditingTab(null);
      setEmploymentSaveMode(null);
      setEditedPersonalDetails(EMPTY_PERSONAL_DETAILS);
      setEditedEmploymentDetails(EMPTY_EMPLOYMENT_DETAILS);
      setEditedPaymentMethodDetails(EMPTY_PAYMENT_METHOD_DETAILS);
      setHasTouchedPaymentMethodValue(false);
      setIsLoadingEmployeeNumber(false);
      resetTemplateRequests();
      setSaveError("");
      setShowLockedEditHint(false);
      return;
    }

    setActiveTab(resetTab);
    setEditingTab(null);
    setEmploymentSaveMode(null);
    setSaveError("");
    setShowLockedEditHint(false);
    setHasTouchedPaymentMethodValue(false);
  }, [initialTab, isOpen, resetTemplateRequests, workerId]);

  useEffect(() => {
    if (!isOpen) return;
    if (editingTab === "profile") return;

    setEditedPersonalDetails((previousValue) => ({
      ...previousValue,
      teamIds:
        profile?.workGroups.map((workGroup) => String(workGroup.id)) ?? [],
    }));
  }, [editingTab, isOpen, profile?.workGroups]);

  useEffect(() => {
    if (editingTab !== "employmentSalary") return;

    const normalizedValues = withNormalizedEmploymentFormByRules(
      {
        salaryType: editedEmploymentDetails.salaryType,
        employeeCategory: editedEmploymentDetails.employeeCategory,
        paymentType: editedEmploymentDetails.paymentType,
      },
      employmentRules,
    );

    if (
      normalizedValues.salaryType === editedEmploymentDetails.salaryType &&
      normalizedValues.employeeCategory ===
        editedEmploymentDetails.employeeCategory &&
      normalizedValues.paymentType === editedEmploymentDetails.paymentType
    ) {
      return;
    }

    setEditedEmploymentDetails((previousValue) => ({
      ...previousValue,
      salaryType: normalizedValues.salaryType,
      employeeCategory: normalizedValues.employeeCategory,
      paymentType: normalizedValues.paymentType,
    }));
  }, [editedEmploymentDetails, editingTab, employmentRules]);

  const updatePersonalField = useCallback(
    <TField extends keyof PersonalDetailsForm>(
      field: TField,
      value: PersonalDetailsForm[TField],
    ): void => {
      const nextValue =
        field === "phoneNumber" && typeof value === "string"
          ? toPhoneDigits(value)
          : value;
      setEditedPersonalDetails((previousValue) => ({
        ...previousValue,
        [field]: nextValue,
      }));
      setSaveError("");
    },
    [],
  );

  const updateEmploymentField = useCallback(
    <TField extends keyof EmploymentDetailsForm>(
      field: TField,
      value: EmploymentDetailsForm[TField],
    ): void => {
      setEditedEmploymentDetails((previousValue) => {
        const nextValue = {
          ...previousValue,
          [field]: value,
        };

        if (field !== "employmentType" || typeof value !== "string") {
          return nextValue;
        }

        if (isPermanentEmployment(value)) {
          return {
            ...nextValue,
            ...toPermanentEmploymentDefaults(companyAddress),
          };
        }

        return nextValue;
      });
    },
    [companyAddress],
  );

  const updatePaymentMethodField = useCallback(
    <TField extends keyof PaymentMethodDetailsForm>(
      field: TField,
      value: PaymentMethodDetailsForm[TField],
    ): void => {
      setEditedPaymentMethodDetails((previousValue) => ({
        ...previousValue,
        [field]: value,
      }));
      if (field === "value") {
        setHasTouchedPaymentMethodValue(true);
      }
      setSaveError("");
    },
    [],
  );

  const fetchEmployeeNumber = useCallback((): void => {
    fetchEmployeeNumberForProfilePanel({
      companyId,
      requestIdRef: employeeNumberRequestIdRef,
      setIsLoadingEmployeeNumber,
      setEditedEmploymentDetails,
    });
  }, [companyId]);

  const handleTabChange = useCallback((value: string): void => {
    if (
      value === "profile" ||
      value === "employmentSalary" ||
      value === "history"
    ) {
      setSaveError("");
      setActiveTab(value);
    }
  }, []);

  const handleEditClick = useCallback((): void => {
    if (activeTab === "profile") {
      setEditedPersonalDetails(toPersonalDetailsForm(profile));
      setEditingTab("profile");
      return;
    }

    if (activeTab === "history") {
      return;
    }

    setEditedEmploymentDetails(toEmploymentDetailsForm(profile));
    setEmploymentSaveMode("update");
    setEditingTab("employmentSalary");
  }, [activeTab, profile]);

  const handleAddEmploymentClick = useCallback((): void => {
    if (activeTab !== "employmentSalary") return;
    const hasActiveEmployment =
      profile?.employments.some((employment) => employment.isActive) === true;
    if (hasActiveEmployment && !hasEndedEmploymentForCreateFlow) {
      openEndEmploymentForCreateModal();
      return;
    }

    const employmentDetails = toEmploymentDetailsForm(profile);
    setEditedEmploymentDetails({
      ...employmentDetails,
      employeeNumber: toEmployeeNumberForNewEmployment(profile),
    });
    setEmploymentSaveMode("create");
    setEditingTab("employmentSalary");
    setSaveError("");
  }, [
    activeTab,
    hasEndedEmploymentForCreateFlow,
    openEndEmploymentForCreateModal,
    profile,
  ]);

  const handleCancelEdit = useCallback((): void => {
    employeeNumberRequestIdRef.current += 1;
    setIsLoadingEmployeeNumber(false);
    resetTemplateRequests();
    setEditingTab(null);
    setEmploymentSaveMode(null);
    setSaveError("");
  }, [resetTemplateRequests]);

  const performSaveEmployment = useCallback(
    (startDate: LocalDate | null, endDate: LocalDate | null): void => {
      runPerformSaveEmployment({
        companyId,
        workerId,
        profile,
        editedEmploymentDetails,
        employmentSaveMode,
        editingTab,
        employmentRules,
        startDate,
        endDate,
        translate: t,
        setIsSaving,
        setSaveError,
        setEditingTab: (): void => setEditingTab(null),
        setEmploymentSaveMode: (): void => setEmploymentSaveMode(null),
        setModalState: (): void => undefined,
        reloadProfile,
        onWorkerUpdated,
      });
    },
    [
      companyId,
      editedEmploymentDetails,
      editingTab,
      employmentSaveMode,
      employmentRules,
      onWorkerUpdated,
      profile,
      reloadProfile,
      t,
      workerId,
    ],
  );

  const handleOpenSaveModal = useCallback((): void => {
    if (editingTab === "profile") {
      if (profile?.isRegistered !== false) return;
      if (companyId == null || workerId == null) return;
      if (!isValidPhoneNumber(editedPersonalDetails.phoneNumber)) {
        setSaveError(t("Staff.AddStaffPanelPhoneNumberInvalid"));
        return;
      }

      savePersonalProfile({
        companyId,
        workerId,
        editedPersonalDetails,
        onWorkerUpdated,
        reloadProfile,
        setEditingTab,
        closeModal: (): void => undefined,
        setIsSaving,
        setSaveError,
        translate: t,
      });
      return;
    }

    if (editingTab !== "employmentSalary") return;

    const currentEmployment = profile?.employmentInfo?.current;
    const nextStartDate =
      editedEmploymentDetails.startDate ??
      toLocalDateOrNull(currentEmployment?.startDate);
    const nextEndDate = editedEmploymentDetails.endDate;

    performSaveEmployment(nextStartDate, nextEndDate);
  }, [
    companyId,
    editedEmploymentDetails.endDate,
    editedEmploymentDetails.startDate,
    editedPersonalDetails,
    editingTab,
    onWorkerUpdated,
    performSaveEmployment,
    profile,
    reloadProfile,
    t,
    workerId,
  ]);
  const isProfilePhoneNumberValid = isValidPhoneNumber(
    editedPersonalDetails.phoneNumber,
  );
  const canSaveEmploymentEdit = useMemo((): boolean => {
    return toCanSaveEmploymentEdit({
      editingTab,
      editedEmploymentDetails,
      isEndDateRequiredOnSave: employmentRules.isEndDateRequiredOnSave,
      employmentSaveMode,
    });
  }, [
    editedEmploymentDetails,
    editingTab,
    employmentRules.isEndDateRequiredOnSave,
    employmentSaveMode,
  ]);

  const paymentMethodError = useMemo((): string => {
    return resolvePaymentMethodError(
      editedPaymentMethodDetails,
      hasTouchedPaymentMethodValue,
      t,
    );
  }, [editedPaymentMethodDetails, hasTouchedPaymentMethodValue, t]);

  const canSavePaymentMethodValue = useMemo((): boolean => {
    return canSavePaymentMethod(editedPaymentMethodDetails);
  }, [editedPaymentMethodDetails]);

  return {
    activeTab,
    editingTab,
    employmentSaveMode,
    editedPersonalDetails,
    editedEmploymentDetails,
    editedPaymentMethodDetails,
    paymentMethodError,
    canSavePaymentMethod: canSavePaymentMethodValue,
    isSaving,
    isLoadingEmployeeNumber,
    employmentTemplates,
    isLoadingEmploymentTemplates,
    selectedEmploymentTemplateId,
    applyEmploymentTemplate,
    templateSalaries: visibleTemplateSalaries,
    selectedTemplateSalaryId,
    applyEmploymentTemplateSalary,
    isSalaryModalOpen,
    isTaxModalOpen,
    isPaymentMethodModalOpen,
    isEndEmploymentModalOpen,
    isEndEmploymentForCreateModalOpen,
    salaryDraftHistory,
    taxDraftHistory,
    effectiveFromDate,
    endEmploymentDate,
    salaryEarliestStartDate: currentEmploymentStartDate,
    saveError,
    showLockedEditHint,
    employmentRules,
    employmentOptions,
    paymentMethodOptions,
    teamOptions,
    isProfilePhoneNumberValid,
    canSaveEmploymentEdit,
    isDiscardConfirmOpen,
    setShowLockedEditHint,
    handleConfirmDiscardClose,
    handleCancelDiscardClose,
    handleTabChange,
    handleEditClick,
    handleAddEmploymentClick,
    handleCancelEdit,
    handleOpenSaveModal,
    handleOpenSalaryModal,
    handleCloseSalaryModal,
    handleConfirmSalaryModal,
    handleAddSalaryModal,
    handleDeleteSalaryModal,
    handleUpdateSalaryDraftValue,
    handleOpenTaxModal,
    handleCloseTaxModal,
    handleConfirmTaxModal,
    handleAddTaxModal,
    handleDeleteTaxModal,
    handleOpenPaymentMethodModal,
    handleClosePaymentMethodModal,
    handleConfirmPaymentMethodModal,
    handleDeletePaymentMethodModal,
    handleOpenEndEmploymentModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
    fetchEmployeeNumber,
    updatePersonalField,
    updateEmploymentField,
    updatePaymentMethodField,
    setEffectiveFromDate,
    setEndEmploymentDate,
  };
};
